import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import type { ReactNode } from 'react'

import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { withKeyboardActivation } from '@/lib/event.util'
import { cn } from '@/lib/utils'

import { AgencyCardFooter } from './agency-card-footer.atom'
import { AgencyCardHeader } from './agency-card-header.atom'
import { AgencyCardStats } from './agency-card-stats.atom'

interface AgencyCardProps {
  agency: Agency
  isPinned?: boolean
  isVisibilityBadgeVisible?: boolean
  onStartChat?: (agency: Agency) => void
  onSeeDetails?: (agency: Agency) => void
  onEdit?: (agency: Agency) => void
  onDelete?: (agency: Agency) => void
  onCardClick?: (agency: Agency) => void
  onPin?: (agency: Agency) => void
  className?: string
}

const AgencyCardBody = ({ agency }: { agency: Agency }): ReactNode => (
  <CardContent className="flex-1 pt-0 pb-3 flex flex-col">
    <AgencyCardStats
      rating={agency.rating}
      reviewCount={agency.reviewCount}
      usageCount={agency.usageCount}
      priceRange={agency.priceRange}
      categoryList={agency.categoryList}
    />
    <p className="agency-card-description text-sm text-muted-foreground line-clamp-3 flex-grow-1">
      {agency.description}
    </p>
  </CardContent>
)

export const AgencyCard = ({
  agency,
  isPinned = false,
  isVisibilityBadgeVisible = false,
  onStartChat,
  onSeeDetails,
  onEdit,
  onDelete,
  onCardClick,
  onPin,
  className,
}: AgencyCardProps): ReactNode => {
  const handleCardClick = (): void => onCardClick?.(agency)

  return (
    <Card
      className={cn(
        'agency-card flex flex-col h-full cursor-pointer transition-shadow hover:shadow-md',
        onCardClick && 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        className
      )}
      onClick={handleCardClick}
      onKeyDown={onCardClick ? withKeyboardActivation(handleCardClick) : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      role={onCardClick ? 'button' : undefined}
    >
      <CardHeader className="pb-0">
        <AgencyCardHeader
          agency={agency}
          isPinned={isPinned}
          isVisibilityBadgeVisible={isVisibilityBadgeVisible}
          onPin={onPin}
        />
      </CardHeader>
      <AgencyCardBody agency={agency} />
      <CardFooter className="pt-0 justify-end gap-2">
        <AgencyCardFooter
          agency={agency}
          onStartChat={onStartChat}
          onSeeDetails={onSeeDetails}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardFooter>
    </Card>
  )
}
